/*
 * Время кадров по часовому поясу арендатора. Сервер отдаёт моменты в UTC,
 * а день, смена и месяц табеля это местные границы Києва
 * (wilco.attendance.zone): у сотрудника, открывшего ноутбук в другом
 * поясе, полночь всё равно киевская.
 */
export const ZONE = "Europe/Kyiv";

const parts = new Intl.DateTimeFormat("en-US", {
  timeZone: ZONE,
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

function local(ms: number): { y: number; mo: number; d: number; h: number; mi: number; s: number } {
  const p = Object.fromEntries(parts.formatToParts(new Date(ms)).map((x) => [x.type, x.value]));
  return { y: Number(p.year), mo: Number(p.month), d: Number(p.day), h: Number(p.hour), mi: Number(p.minute), s: Number(p.second) };
}

/** Смещение Києва от UTC в этот момент, мс: +2 ч зимой, +3 ч летом. */
function offset(ms: number): number {
  const l = local(ms);
  return Date.UTC(l.y, l.mo - 1, l.d, l.h, l.mi, l.s) - Math.floor(ms / 1000) * 1000;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** Местная дата YYYY-MM-DD момента. */
export function localDate(ms: number = Date.now()): string {
  const l = local(ms);
  return `${String(l.y)}-${pad(l.mo)}-${pad(l.d)}`;
}

/** Местное время HH:mm момента ISO. */
export function localTime(iso: string): string {
  const l = local(Date.parse(iso));
  return `${pad(l.h)}:${pad(l.mi)}`;
}

/** Момент ISO UTC для местных даты и времени HH:mm. Второй проход ловит смену летнего времени. */
export function instant(day: string, time: string): string {
  const [y, mo, d] = day.split("-").map(Number);
  const [h, mi] = time.split(":").map(Number);
  const guess = Date.UTC(y ?? 1970, (mo ?? 1) - 1, d ?? 1, h ?? 0, mi ?? 0);
  let ms = guess - offset(guess);
  ms = guess - offset(ms);
  return new Date(ms).toISOString();
}

export function addDays(day: string, n: number): string {
  const [y, mo, d] = day.split("-").map(Number);
  const ms = Date.UTC(y ?? 1970, (mo ?? 1) - 1, (d ?? 1) + n);
  return new Date(ms).toISOString().slice(0, 10);
}

/** Местные сутки [from, to) моментами для GET /attendance/shifts. */
export function dayRange(day: string): { from: string; to: string } {
  return { from: instant(day, "00:00"), to: instant(addDays(day, 1), "00:00") };
}

/** Прошлый месяц YYYY-MM: табель по умолчанию за него, как у сервера. */
export function previousMonth(ms: number = Date.now()): string {
  const l = local(ms);
  const m = l.mo === 1 ? 12 : l.mo - 1;
  return `${String(l.mo === 1 ? l.y - 1 : l.y)}-${pad(m)}`;
}

/** Длительность минутами «8:05»: часы и минуты, как в табеле на бумаге. */
export function hm(minutes: number): string {
  const sign = minutes < 0 ? "−" : "";
  const a = Math.abs(minutes);
  return `${sign}${String(Math.floor(a / 60))}:${pad(a % 60)}`;
}

/** Минуты между моментами ISO. */
export function minutesBetween(from: string, to: string): number {
  return Math.round((Date.parse(to) - Date.parse(from)) / 60_000);
}

const dayFormat = new Intl.DateTimeFormat("uk", { weekday: "short", day: "2-digit", month: "2-digit", timeZone: "UTC" });

/** «вт, 03.11» для местной даты YYYY-MM-DD. */
export function formatDay(day: string): string {
  return dayFormat.format(new Date(`${day}T00:00:00Z`));
}

const monthFormat = new Intl.DateTimeFormat("uk", { month: "long", year: "numeric", timeZone: "UTC" });

/** «жовтень 2026 р.» для YYYY-MM. */
export function formatMonth(month: string): string {
  return monthFormat.format(new Date(`${month}-01T00:00:00Z`));
}
